import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Alert,
  Fab,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Delete as DeleteIcon, PhotoCamera as PhotoCameraIcon, CameraAlt as CameraAltIcon } from '@mui/icons-material';
import { BlobStorageTypeEnumValue } from 'enums/BlobStorageTypeEnum';
import { SubmissionDetailStatusEnum, SubmissionDetailStatusEnumValue } from 'enums/SubmissionDetailStatusEnum';
import { CertificationStatusEnumValue } from 'enums/CertificationStatusEnum';
import { useSubmission, useUpdateSubmission } from 'services/Data/SubmissionServices';
import {
  useCreateSubmissionDetail,
  useDeleteSubmissionDetail,
  useUpdateSubmissionDetail,
} from 'services/Data/SubmissionDetailServices';
import { useUploadSubmissionDetailFiles, useDeleteAttachedFile } from 'services/Data/AttachedFileServices';
import { createSubmissionPatchDocument } from 'services/Serializers/SubmissionPatchDocument';
import { createSubmissionDetailPatchDocument } from 'services/Serializers/SubmissionDetailPatchDocument';
import { translateLabel } from 'services/Utilities/labelUtils';
import { HOST, UPLOAD_MAX_IMAGE_SIZE } from 'config/config';
import SocialFeed from 'Components/SocialFeed';
import RenderItem from './RenderItem';
import useMobile from 'services/Hooks/useMobile';
import { heicTo, isHeic } from 'heic-to/csp';

const HEIC_EXTENSIONS = new Set(['heic', 'heif']);
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'heif', 'mif1', 'msf1']);

const isHeicFileType = (fileName, mimeType) => {
  const extension = fileName
    ?.split('.')
    ?.pop()
    ?.toLowerCase();
  const normalizedMimeType = mimeType?.toLowerCase();
  return HEIC_EXTENSIONS.has(extension) || HEIC_MIME_TYPES.has(normalizedMimeType);
};

const getBlobFormat = async blob => {
  if (!blob) {
    return 'unknown';
  }

  const normalizedMimeType = blob.type?.toLowerCase();
  if (HEIC_MIME_TYPES.has(normalizedMimeType)) {
    return 'heif';
  }
  if (normalizedMimeType === 'image/jpeg') {
    return 'jpeg';
  }
  if (normalizedMimeType === 'image/png') {
    return 'png';
  }

  try {
    const headerBuffer = await blob.slice(0, 64).arrayBuffer();
    const bytes = new Uint8Array(headerBuffer);

    if (bytes.length >= 12) {
      const boxType = String.fromCharCode(...bytes.slice(4, 8));
      if (boxType === 'ftyp') {
        const majorBrand = String.fromCharCode(...bytes.slice(8, 12)).toLowerCase();
        if (HEIF_BRANDS.has(majorBrand)) {
          return 'heif';
        }

        for (let offset = 16; offset + 4 <= bytes.length; offset += 4) {
          const compatibleBrand = String.fromCharCode(...bytes.slice(offset, offset + 4)).toLowerCase();
          if (HEIF_BRANDS.has(compatibleBrand)) {
            return 'heif';
          }
        }
      }
    }

    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return 'jpeg';
    }
  } catch (error) {
    return 'unknown';
  }

  return 'unknown';
};

const EMPTY_FILES = [];

// Photo card with upload/delete and nested children rendering
const SubmissionDetailCard = ({ detail, allDetails = [], isReadOnly = false }) => {
  const { submissionId } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMobile();

  const { data: submission } = useSubmission(submissionId);
  const uploadFilesMutation = useUploadSubmissionDetailFiles(submissionId);
  const deleteFileMutation = useDeleteAttachedFile(submissionId);
  const deleteSubmissionDetailMutation = useDeleteSubmissionDetail(submissionId);
  const createSubmissionDetailMutation = useCreateSubmissionDetail(submissionId);
  const updateSubmissionDetailMutation = useUpdateSubmissionDetail(submissionId);
  const updateSubmissionMutation = useUpdateSubmission(submissionId);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadingDetailId, setUploadingDetailId] = useState(null);
  const [deletingDetailId, setDeletingDetailId] = useState(null);
  const [localUploads, setLocalUploads] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isConvertingPreview, setIsConvertingPreview] = useState(false);
  const [pendingReplacement, setPendingReplacement] = useState(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');

  const existingSubmissionDetail = submission?.submissionDetails?.find(sd => sd.criteriaTypeDetail_Id === detail.id);
  const isPhotoRequired = detail?.isRequired !== false;
  const isAccepted = existingSubmissionDetail?.status === SubmissionDetailStatusEnumValue.Accepted;
  const statusConfig = existingSubmissionDetail
    ? SubmissionDetailStatusEnum.find(s => s.value === existingSubmissionDetail.status)
    : null;
  const isFinalSubmissionStatus =
    submission?.status === CertificationStatusEnumValue.Passed ||
    submission?.status === CertificationStatusEnumValue.PassedForAnotherOption ||
    submission?.status === CertificationStatusEnumValue.Failed;
  const isStatusChipInProgress =
    existingSubmissionDetail?.status === SubmissionDetailStatusEnumValue.Pending || statusConfig?.name === 'InProgress';
  const shouldShowStatusChip = Boolean(statusConfig) && !(isFinalSubmissionStatus && isStatusChipInProgress);

  const childDetails = useMemo(() => {
    return allDetails
      .filter(d => d.parentCriteriaTypeDetail_Id === detail.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [allDetails, detail.id]);

  const isUploading = uploadingDetailId === detail.id;
  const isDeleting = deletingDetailId === detail.id;
  const canEditPhotos = !isReadOnly;
  const canDeleteExisting = canEditPhotos && existingSubmissionDetail?.status === SubmissionDetailStatusEnumValue.Pending;

  const statusAccentColor =
    existingSubmissionDetail?.status === SubmissionDetailStatusEnumValue.Accepted
      ? '#66BB6A'
      : existingSubmissionDetail?.status === SubmissionDetailStatusEnumValue.NeedsWork
      ? '#EF5350'
      : existingSubmissionDetail?.status === SubmissionDetailStatusEnumValue.Reworked
      ? '#aece21ff'
      : null;

  const cardBorderColor = statusAccentColor
    ? `${statusAccentColor}80`
    : theme.palette.mode === 'dark'
    ? 'rgba(255, 210, 0, 0.15)'
    : 'rgba(51, 49, 50, 0.12)';

  const hoverBorderColor = statusAccentColor
    ? statusAccentColor
    : theme.palette.mode === 'dark'
    ? 'rgba(255, 210, 0, 0.4)'
    : 'rgba(212, 175, 55, 0.4)';

  const hoverShadow = statusAccentColor
    ? `0 6px 20px ${statusAccentColor}40`
    : theme.palette.mode === 'dark'
    ? '0 4px 16px rgba(255, 210, 0, 0.15)'
    : '0 4px 16px rgba(212, 175, 55, 0.12)';

  const getFilePreviewUrl = file => {
    const blobType = file?.blobStorageType || file?.blobStorageType_Id || BlobStorageTypeEnumValue.CertificationTool;
    const entityId = existingSubmissionDetail?.id || file?.submissionDetail_Id || file?.entityId;
    if (entityId && file?.id) {
      return `${HOST}/api/dashboard/attached-files/${entityId}/${file.id}/download?type=${blobType}`;
    }
    return file?.blobUrl || file?.url || file?.path || '';
  };

  const hasAnyPhotos = () => {
    if (!submission?.submissionDetails) return false;
    return submission.submissionDetails.some(sd => sd.files && sd.files.length > 0);
  };

  const ensureSubmissionDetail = async () => {
    if (existingSubmissionDetail) return existingSubmissionDetail;
    const newSubmissionDetail = {
      id: crypto.randomUUID(),
      submission_Id: submissionId,
      criteriaTypeDetail_Id: detail.id,
      description: '',
    };
    return createSubmissionDetailMutation.mutateAsync(newSubmissionDetail);
  };

  const updateSubmissionStatus = async newStatus => {
    const patchDoc = createSubmissionPatchDocument({ status: newStatus });
    await updateSubmissionMutation.mutateAsync(patchDoc);
  };

  const handleFileUpload = async files => {
    if (!canEditPhotos || !files || files.length === 0) return;
    const fileArray = Array.from(files);
    const oversizedFile = fileArray.find(file => file.size > UPLOAD_MAX_IMAGE_SIZE);

    if (oversizedFile) {
      const maxSizeMb = Math.round(UPLOAD_MAX_IMAGE_SIZE / (1024 * 1024));
      setUploadErrorMessage(t('submission.fileTooLarge', { maxSizeMb }));
      return;
    }

    setUploadErrorMessage('');
    const hadExistingPhoto = Boolean(existingSubmissionDetail?.files?.length);
    let uploadSucceeded = false;

    try {
      setUploadingDetailId(detail.id);
      setPendingReplacement({
        detailId: detail.id,
        previousFileId: existingSubmissionDetail?.files?.[0]?.id || null,
      });
      setLocalUploads(prev => ({ ...prev, [detail.id]: fileArray }));
      const submissionDetail = await ensureSubmissionDetail();

      await uploadFilesMutation.mutateAsync({
        submissionDetailId: submissionDetail.id,
        files: fileArray,
        blobStorageType: BlobStorageTypeEnumValue.CertificationTool,
      });

      if (hadExistingPhoto) {
        const patchDoc = createSubmissionDetailPatchDocument({ status: SubmissionDetailStatusEnumValue.Reworked });
        await updateSubmissionDetailMutation.mutateAsync({
          submissionDetailId: submissionDetail.id,
          patchDocument: patchDoc,
        });
      }

      if (submission?.status === CertificationStatusEnumValue.Pending && !hasAnyPhotos()) {
        await updateSubmissionStatus(CertificationStatusEnumValue.InProgress);
      }

      uploadSucceeded = true;
    } catch (error) {
      setUploadErrorMessage(t('submission.uploadFailed'));
      console.error('File upload failed:', error);
    } finally {
      setUploadingDetailId(null);

      if (!uploadSucceeded) {
        setPendingReplacement(null);
        setLocalUploads(prev => ({ ...prev, [detail.id]: [] }));
      }
    }
  };

  const handleDeleteFile = (submissionDetailId, fileId) => {
    if (!canDeleteExisting) return;
    setDeleteTarget({ submissionDetailId, fileId });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { submissionDetailId, fileId } = deleteTarget;
    try {
      setDeletingDetailId(submissionDetailId);
      const detailWithFile = submission?.submissionDetails?.find(sd => sd.id === submissionDetailId);
      const remainingFiles = (detailWithFile?.files?.length || 0) - 1;
      const isLastFileInDetail = remainingFiles === 0;

      await deleteFileMutation.mutateAsync({ entityId: submissionDetailId, attachedFileId: fileId });

      if (isLastFileInDetail) {
        await deleteSubmissionDetailMutation.mutateAsync(submissionDetailId);
      }

      const otherDetailsWithFiles =
        submission?.submissionDetails?.filter(sd => sd.id !== submissionDetailId && sd.files && sd.files.length > 0)
          .length || 0;
      const willHaveNoPhotos = isLastFileInDetail && otherDetailsWithFiles === 0;

      if (submission?.status === CertificationStatusEnumValue.InProgress && willHaveNoPhotos) {
        await updateSubmissionStatus(CertificationStatusEnumValue.Pending);
      }
    } catch (error) {
      console.error('File deletion failed:', error);
    } finally {
      setDeleteTarget(null);
      setDeletingDetailId(null);
    }
  };

  const isFileDrag = event => {
    const dataTransfer = event?.dataTransfer;
    if (!dataTransfer) return false;
    if (dataTransfer.files && dataTransfer.files.length > 0) return true;
    const types = dataTransfer.types;
    return types ? Array.from(types).includes('Files') : false;
  };

  const handleDragEnter = event => {
    if (!isFileDrag(event) || !canEditPhotos || isUploading || isDeleting) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragOver = event => {
    if (!isFileDrag(event) || !canEditPhotos || isUploading || isDeleting) return;
    event.preventDefault();
    event.stopPropagation();
    if (!isDragActive) setIsDragActive(true);
  };

  const handleDragLeave = event => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const nextTarget = event.relatedTarget;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    setIsDragActive(false);
  };

  const handleDrop = event => {
    if (!isFileDrag(event) || !canEditPhotos || isUploading || isDeleting) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    handleFileUpload(event.dataTransfer.files);
  };

  const localFiles = useMemo(() => localUploads[detail.id] || EMPTY_FILES, [localUploads, detail.id]);
  const localFile = localFiles[0] || null;
  const existingFile = existingSubmissionDetail?.files?.[0] || null;

  const previewSourceKey = useMemo(() => {
    if (localFile) {
      return `local:${localFile.name || ''}:${localFile.size || 0}:${localFile.lastModified || 0}`;
    }

    if (existingFile) {
      return `existing:${existingSubmissionDetail?.id || ''}:${existingFile.id || ''}:${existingFile.fileName || ''}`;
    }

    return 'none';
  }, [existingFile, existingSubmissionDetail?.id, localFile]);

  const hasLocalPreview = Boolean(localFiles[0]);

  useEffect(() => {
    if (!pendingReplacement || pendingReplacement.detailId !== detail.id) {
      return;
    }

    if (isUploading) {
      return;
    }

    const currentServerFileId = existingFile?.id || null;
    const hasServerFileSwapped =
      Boolean(currentServerFileId) && currentServerFileId !== (pendingReplacement.previousFileId || null);

    if (hasServerFileSwapped) {
      setPendingReplacement(null);
      setLocalUploads(prev => ({ ...prev, [detail.id]: [] }));
    }
  }, [detail.id, existingFile?.id, isUploading, pendingReplacement]);

  useEffect(() => {
    let isMounted = true;
    let generatedObjectUrl = null;

    const setPreviewFromBlob = async blob => {
      const canDecodeHeif = await isHeic(blob);

      if (!canDecodeHeif) {
        const passthroughUrl = URL.createObjectURL(blob);
        generatedObjectUrl = passthroughUrl;
        if (isMounted) {
          setPreviewUrl(passthroughUrl);
        }
        return;
      }

      const converted = await heicTo({
        blob,
        type: 'image/jpeg',
        quality: 0.9,
      });

      const convertedBlob = converted instanceof Blob ? converted : new Blob([converted], { type: 'image/jpeg' });
      const convertedUrl = URL.createObjectURL(convertedBlob);
      generatedObjectUrl = convertedUrl;

      if (isMounted) {
        setPreviewUrl(convertedUrl);
      }
    };

    const resolvePreview = async () => {
      if (!localFile && !existingFile) {
        if (isMounted) {
          setPreviewUrl(null);
          setIsConvertingPreview(false);
        }
        return;
      }

      if (localFile) {
        const isLocalHeic = isHeicFileType(localFile.name, localFile.type);

        if (!isLocalHeic) {
          const localObjectUrl = URL.createObjectURL(localFile);
          generatedObjectUrl = localObjectUrl;
          if (isMounted) {
            setPreviewUrl(localObjectUrl);
            setIsConvertingPreview(false);
          }
          return;
        }

        if (isMounted) {
          setIsConvertingPreview(true);
        }

        try {
          await setPreviewFromBlob(localFile);
        } catch (error) {
          const fallbackLocalObjectUrl = URL.createObjectURL(localFile);
          generatedObjectUrl = fallbackLocalObjectUrl;
          if (isMounted) {
            setPreviewUrl(fallbackLocalObjectUrl);
          }
        } finally {
          if (isMounted) {
            setIsConvertingPreview(false);
          }
        }

        return;
      }

      const downloadUrl = getFilePreviewUrl(existingFile);
      const isExistingHeicByMeta = isHeicFileType(
        existingFile?.fileName,
        existingFile?.contentType || existingFile?.mimeType,
      );

      if (!downloadUrl) {
        if (isMounted) {
          setPreviewUrl(null);
          setIsConvertingPreview(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setIsConvertingPreview(true);
        }

        const response = await fetch(downloadUrl);
        const blob = await response.blob();

        const blobFormat = await getBlobFormat(blob);
        const isExistingHeicByBlob = blobFormat === 'heif' || isHeicFileType(existingFile?.fileName, blob?.type);

        if (isExistingHeicByMeta || isExistingHeicByBlob) {
          await setPreviewFromBlob(blob);
        } else if (isMounted) {
          setPreviewUrl(downloadUrl);
        }
      } catch (error) {
        if (isMounted) {
          setPreviewUrl(downloadUrl);
        }
      } finally {
        if (isMounted) {
          setIsConvertingPreview(false);
        }
      }
    };

    resolvePreview();

    return () => {
      isMounted = false;
      if (generatedObjectUrl) {
        URL.revokeObjectURL(generatedObjectUrl);
      }
    };
  }, [existingFile, localFile, previewSourceKey]);

  const dropBorderColor = theme.palette.mode === 'dark' ? theme.palette.primary.main : '#B8941E';
  const canAcceptDrop = canEditPhotos && !uploadFilesMutation.isPending && !isUploading && !isDeleting;

  if (!submission) return null;
  const hasExistingPhotos = existingSubmissionDetail?.files?.length > 0;
  const extraCount = hasLocalPreview
    ? Math.max((localFiles?.length || 0) - 1, 0)
    : Math.max((existingSubmissionDetail?.files?.length || 0) - 1, 0);
  const clearLocalPreview = () => setLocalUploads(prev => ({ ...prev, [detail.id]: [] }));

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${cardBorderColor}`,
        transition: 'all 0.3s ease',
        '&:hover': { borderColor: hoverBorderColor, boxShadow: hoverShadow },
      }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ width: '100%' }}>
          <Alert
            icon={false}
            severity="info"
            action={
              <Chip
                data-tour="submissionRequiredTag"
                label={t(isPhotoRequired ? 'submission.requiredPhoto' : 'submission.optionalPhoto')}
                size="small"
                sx={{
                  fontWeight: 700,
                  px: 1,
                  color: theme => (isPhotoRequired ? theme.palette.info.dark : theme.palette.text.disabled),
                  border: theme => `1px solid ${isPhotoRequired ? theme.palette.info.main : theme.palette.divider}`,
                  bgcolor: theme =>
                    isPhotoRequired
                      ? theme.palette.mode === 'dark'
                        ? 'rgba(2, 136, 209, 0.2)'
                        : 'rgba(2, 136, 209, 0.08)'
                      : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                  '& .MuiChip-icon': {
                    color: theme => (isPhotoRequired ? theme.palette.info.dark : theme.palette.text.disabled),
                  },
                }}
              />
            }
            sx={{
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '& .MuiAlert-message': {
                width: '100%',
                pr: { xs: 0, sm: 1 },
              },
              '& .MuiAlert-action': {
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                pt: { xs: 1, sm: 0 },
                pl: { xs: 0, sm: 2 },
              },
            }}>
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {translateLabel(detail.name, i18n.language)}
            </Typography>
            {detail.description && (
              <Typography variant="body1" color="text.caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                {translateLabel(detail.description, i18n.language)}
              </Typography>
            )}
          </Alert>
        </Box>

        <Divider />

        {uploadErrorMessage && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadErrorMessage('')}>
            {uploadErrorMessage}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 360 }}>
          <Box
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              position: 'relative',
              width: { xs: '100%', md: '55%' },
              minHeight: { xs: 320, md: 420 },
              bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#FAFAFA',
              borderRadius: 2,
              overflow: 'hidden',
              outline: isDragActive && canAcceptDrop ? `2px dashed ${dropBorderColor}` : 'none',
              outlineOffset: -12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            {previewUrl || isConvertingPreview ? (
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                {previewUrl ? (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt={translateLabel(detail.name, i18n.language)}
                    onClick={() => setViewerUrl(previewUrl)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      maxHeight: 450,
                      objectFit: 'cover',
                      filter: isUploading || isDeleting ? 'brightness(0.6)' : 'none',
                      cursor: isDeleting ? 'default' : 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': !isDeleting ? { transform: 'scale(1.01)' } : {},
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      minHeight: { xs: 320, md: 420 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.45)',
                    }}>
                    <CircularProgress
                      size={42}
                      sx={{ color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#FFD200' }}
                    />
                  </Box>
                )}

                {extraCount > 0 && (
                  <Chip
                    label={`+${extraCount}`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.75)',
                      color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#FFD200',
                      fontWeight: 700,
                      backdropFilter: 'blur(4px)',
                    }}
                  />
                )}

                {isDeleting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.55)',
                      gap: 1,
                      flexDirection: 'column',
                    }}>
                    <CircularProgress size={42} sx={{ color: '#d32f2f' }} />
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                      {t('common.deleting')}...
                    </Typography>
                  </Box>
                )}

                {isUploading && !isDeleting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.45)',
                      gap: 1,
                      flexDirection: 'column',
                    }}>
                    <CircularProgress
                      size={42}
                      sx={{ color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#FFD200' }}
                    />
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                      {t('common.uploading')}...
                    </Typography>
                  </Box>
                )}

                {isConvertingPreview && !isUploading && !isDeleting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0,0,0,0.35)',
                    }}>
                    <CircularProgress
                      size={32}
                      sx={{ color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#FFD200' }}
                    />
                  </Box>
                )}

                {!isDeleting && hasLocalPreview && canEditPhotos && (
                  <IconButton
                    size="small"
                    onClick={clearLocalPreview}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      bgcolor: 'rgba(211, 47, 47, 0.85)',
                      color: '#fff',
                      '&:hover': { bgcolor: '#d32f2f' },
                    }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}

                {!isDeleting && !hasLocalPreview && hasExistingPhotos && canDeleteExisting && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteFile(existingSubmissionDetail?.id, existingSubmissionDetail.files[0].id)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 32,
                      height: 32,
                      bgcolor: 'rgba(211, 47, 47, 0.85)',
                      color: '#fff',
                      '&:hover': { bgcolor: '#d32f2f' },
                    }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}

                {!isDeleting && canEditPhotos && (
                  <Fab
                    component="label"
                    variant="extended"
                    size="small"
                    disabled={!canEditPhotos || uploadFilesMutation.isPending}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      px: 1.25,
                      gap: 0.75,
                      height: 36,
                      minHeight: 36,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.9)' : 'rgba(255, 210, 0, 0.9)',
                      color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#000',
                    }}>
                    <CameraAltIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1 }}>
                      {t('submission.replacePhoto')}
                    </Typography>
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      disabled={!canEditPhotos || uploadFilesMutation.isPending}
                      onChange={event => {
                        handleFileUpload(event.target.files);
                        event.target.value = '';
                      }}
                    />
                  </Fab>
                )}
              </Box>
            ) : (
              <Button
                component="label"
                disabled={!canEditPhotos || uploadFilesMutation.isPending}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: { xs: 320, md: 420 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  textAlign: 'center',
                  color: theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.5)' : 'rgba(212, 175, 55, 0.7)',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(212, 175, 55, 0.08)' : 'rgba(212, 175, 55, 0.06)',
                    color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#B8941E',
                  },
                }}>
                {uploadFilesMutation.isPending ? (
                  <CircularProgress
                    size={48}
                    sx={{ color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#B8941E' }}
                  />
                ) : (
                  <>
                    <PhotoCameraIcon sx={{ fontSize: 48 }} />
                    <Typography variant="body2">{t('submission.uploadPhotos')}</Typography>
                    {isMobile ? (
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('submission.tapOrClick')}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {t('submission.dragOrClick')}
                      </Typography>
                    )}
                  </>
                )}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  disabled={!canEditPhotos || uploadFilesMutation.isPending}
                  onChange={event => {
                    handleFileUpload(event.target.files);
                    event.target.value = '';
                  }}
                />
              </Button>
            )}

            {isDragActive && canAcceptDrop && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.85)',
                  border: `2px dashed ${dropBorderColor}`,
                  pointerEvents: 'none',
                  color: theme.palette.mode === 'dark' ? dropBorderColor : '#000',
                  textAlign: 'center',
                  px: 2,
                }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('submission.dropPhotosHere')}
                </Typography>
                <Typography variant="body2">{t('submission.dragOrClick')}</Typography>
              </Box>
            )}
          </Box>

          {childDetails.length > 0 && (
            <Box
              sx={{
                flex: { xs: '1 1 auto', md: '0 0 45%' },
                border: theme => `1px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                bgcolor: theme => theme.palette.background.paper,
                minHeight: 240,
              }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t('submission.additionalDetails')}
              </Typography>
              <Stack spacing={1.5}>
                {childDetails.map(childDetail => (
                  <RenderItem key={childDetail.id} detail={childDetail} allDetails={allDetails} isReadOnly={isReadOnly} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </CardContent>

      {shouldShowStatusChip && (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', px: 3, pb: 4 }}>
          <Chip
            icon={statusConfig.icon ? <statusConfig.icon sx={{ fontSize: 20 }} /> : null}
            label={i18n.language === 'fr' ? statusConfig.labelFrench : statusConfig.labelEnglish}
            size="medium"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? alpha(statusConfig.bgColor, 0.2) : statusConfig.bgColor,
              color: statusConfig.color,
              fontWeight: 700,
              px: 1.5,
              boxShadow: theme => (theme.palette.mode === 'dark' ? theme.shadows[3] : theme.shadows[1]),
              border: theme =>
                theme.palette.mode === 'dark'
                  ? `1px solid ${alpha(statusConfig.bgColor, 0.3)}`
                  : `1px solid ${theme.palette.divider}`,
              '& .MuiChip-icon': {
                color: statusConfig.color,
              },
            }}
          />
        </Box>
      )}

      {existingSubmissionDetail && hasExistingPhotos && (
        <Box
          sx={{
            width: '100%',
            p: 3,
            bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#FFFFFF',
            borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 210, 0, 0.15)' : 'rgba(51, 49, 50, 0.12)'}`,
          }}>
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: 600, color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#B8941E' }}>
            {t('submission.comments')}
          </Typography>
          <SocialFeed
            submissionId={submissionId}
            submissionDetailId={existingSubmissionDetail.id}
            posts={existingSubmissionDetail.posts || []}
            isReadOnly={isAccepted || isReadOnly}
          />
        </Box>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>
          <Typography>{t('submission.confirmDeleteTitle')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>{t('submission.confirmDeleteImage')}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteFileMutation.isPending || deletingDetailId === deleteTarget?.submissionDetailId}
            startIcon={deleteFileMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {t('common.delete')}
          </Button>
          <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewerUrl)} onClose={() => setViewerUrl('')} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {viewerUrl ? <Box component="img" src={viewerUrl} alt="preview" sx={{ width: '100%', display: 'block' }} /> : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

SubmissionDetailCard.defaultProps = {
  isReadOnly: false,
};

SubmissionDetailCard.propTypes = {};

export default SubmissionDetailCard;
